import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAuth } from '../hooks/useAuth'
import { User, Lock, Save, AlertCircle, CheckCircle } from 'lucide-react'
import { updateUsername, updatePassword, updateThemeColors } from '../services/authService'
import ProfileImageUploader from '../components/ProfileImageUploader'
import ThemeColorPicker from '../components/ThemeColorPicker'
import { HSLColor, getDefaultColors, calculateAccentColor } from '../utils/colorManagement'
import { useTheme } from '../hooks/useTheme'

const profileSchema = z.object({
  username: z.string().min(3, 'Username must be at least 3 characters'),
})

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string().min(8, 'Password must be at least 8 characters'),
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})

type ProfileFormData = z.infer<typeof profileSchema>
type PasswordFormData = z.infer<typeof passwordSchema>

const SettingsPage: React.FC = () => {
  const { user } = useAuth()
  const { theme } = useTheme()
  const isDarkMode = theme === 'dark'
  
  const [profileSuccess, setProfileSuccess] = useState(false)
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [profileUpdated, setProfileUpdated] = useState(false)
  
  const { 
    register: registerProfile, 
    handleSubmit: handleProfileSubmit, 
    formState: { errors: profileErrors, isSubmitting: isProfileSubmitting } 
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      username: user?.username || '',
    }
  })
  
  const { 
    register: registerPassword, 
    handleSubmit: handlePasswordSubmit, 
    formState: { errors: passwordErrors, isSubmitting: isPasswordSubmitting },
    reset: resetPassword
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  })
  
  const onProfileSubmit = async (data: ProfileFormData) => {
    setProfileSuccess(false)
    setProfileError(null)
    
    try {
      if (!user) return
      
      // Call the API to update the username
      const updatedUser = await updateUsername(user.id, data.username)
      
      if (updatedUser) {
        setProfileSuccess(true)
      } else {
        setProfileError('Failed to update profile')
      }
    } catch (error) {
      setProfileError('Failed to update profile')
      console.error(error)
    }
  }
  
  const onPasswordSubmit = async (data: PasswordFormData) => {
    setPasswordSuccess(false)
    setPasswordError(null)
    
    try {
      if (!user) return
      
      // Call the API to update the password
      const success = await updatePassword(
        user.id, 
        data.currentPassword, 
        data.newPassword
      )
      
      if (success) {
        setPasswordSuccess(true)
        resetPassword()
      } else {
        setPasswordError('Current password is incorrect')
      }
    } catch (error) {
      setPasswordError('Failed to update password')
      console.error(error)
    }
  }
  
  const handleColorChange = async (colors: { secondary: HSLColor; accent: HSLColor }) => {
    if (!user) return
    
    try {
      await updateThemeColors(user.id, { secondary: colors.secondary })
      
      // Update CSS variables
      document.documentElement.style.setProperty(
        '--color-secondary',
        `hsl(${colors.secondary.h}deg ${colors.secondary.s}% ${colors.secondary.l}%)`
      )
      document.documentElement.style.setProperty(
        '--color-accent',
        `hsl(${colors.accent.h}deg ${colors.accent.s}% ${colors.accent.l}%)`
      )
    } catch (error) {
      console.error('Failed to update theme colors:', error)
    }
  }

  const handleProfileImageUpdate = () => {
    setProfileUpdated(prev => !prev)
  }
  
  if (!user) return null
  
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Settings</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Profile Settings */}
        <div className="bg-white dark:bg-[#2a2a2a] rounded-lg shadow-custom-light p-6">
          <h2 className="text-xl font-semibold mb-4">Profile Settings</h2>
          
          <ProfileImageUploader 
            userId={user.id} 
            onImageUpdate={handleProfileImageUpdate}
          />
          
          <div className="border-t border-[var(--color-accent)] dark:border-[var(--color-secondary)] border-opacity-20 dark:border-opacity-30 pt-6 mt-2">
            {profileSuccess && (
              <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md flex items-center">
                <CheckCircle size={18} className="mr-2 flex-shrink-0" />
                <span>Profile updated successfully</span>
              </div>
            )}
            
            {profileError && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md flex items-center">
                <AlertCircle size={18} className="mr-2 flex-shrink-0" />
                <span>{profileError}</span>
              </div>
            )}
            
            <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-4">
              <div>
                <label htmlFor="username" className="block mb-1 font-medium text-[var(--color-primary)]">
                  Username
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <User size={18} className="text-[var(--color-accent)]" />
                  </div>
                  <input
                    id="username"
                    type="text"
                    className="input pl-10 w-full"
                    placeholder="Enter your username"
                    {...registerProfile('username')}
                  />
                </div>
                {profileErrors.username && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{profileErrors.username.message}</p>
                )}
              </div>
              
              <button
                type="submit"
                className="btn btn-primary w-full flex items-center justify-center"
                disabled={isProfileSubmitting}
              >
                {isProfileSubmitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    Save Profile
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
        
        {/* Password Settings */}
        <div className="bg-white dark:bg-[#2a2a2a] rounded-lg shadow-custom-light p-6">
          <h2 className="text-xl font-semibold mb-4">Change Password</h2>
          
          {passwordSuccess && (
            <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md flex items-center">
              <CheckCircle size={18} className="mr-2 flex-shrink-0" />
              <span>Password updated successfully</span>
            </div>
          )}
          
          {passwordError && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md flex items-center">
              <AlertCircle size={18} className="mr-2 flex-shrink-0" />
              <span>{passwordError}</span>
            </div>
          )}
          
          <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4">
            <div>
              <label htmlFor="currentPassword" className="block mb-1 font-medium text-[var(--color-primary)]">
                Current Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Lock size={18} className="text-[var(--color-accent)]" />
                </div>
                <input
                  id="currentPassword"
                  type="password"
                  className="input pl-10 w-full"
                  placeholder="Enter your current password"
                  {...registerPassword('currentPassword')}
                />
              </div>
              {passwordErrors.currentPassword && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{passwordErrors.currentPassword.message}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="newPassword" className="block mb-1 font-medium text-[var(--color-primary)]">
                New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Lock size={18} className="text-[var(--color-accent)]" />
                </div>
                <input
                  id="newPassword"
                  type="password"
                  className="input pl-10 w-full"
                  placeholder="Enter your new password"
                  {...registerPassword('newPassword')}
                />
              </div>
              {passwordErrors.newPassword && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{passwordErrors.newPassword.message}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block mb-1 font-medium text-[var(--color-primary)]">
                Confirm New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Lock size={18} className="text-[var(--color-accent)]" />
                </div>
                <input
                  id="confirmPassword"
                  type="password"
                  className="input pl-10 w-full"
                  placeholder="Confirm your new password"
                  {...registerPassword('confirmPassword')}
                />
              </div>
              {passwordErrors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{passwordErrors.confirmPassword.message}</p>
              )}
            </div>
            
            <button
              type="submit"
              className="btn btn-primary w-full flex items-center justify-center"
              disabled={isPasswordSubmitting}
            >
              {isPasswordSubmitting ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  Update Password
                </>
              )}
            </button>
          </form>
        </div>
        
        {/* Theme Color Settings */}
        <div className="bg-white dark:bg-[#2a2a2a] rounded-lg shadow-custom-light p-6">
          <h2 className="text-xl font-semibold mb-4">Theme Colors</h2>
          
          <ThemeColorPicker
            onColorChange={handleColorChange}
            initialSecondary={user.themeColors?.secondary || getDefaultColors(isDarkMode).secondary}
          />
        </div>
      </div>
    </div>
  )
}

export default SettingsPage